import { toast } from "react-toastify";

/**
 * Toast Service - Centralized notification system using react-toastify
 * Supports theme-aware styling and custom icons
 */
class ToastService {
  constructor() {
    this.currentTheme = null;
  }

  /**
   * Set the current theme for toasts
   * @param {string} theme - Theme name (e.g., "theme-0", "theme-1", etc.)
   */
  setTheme(theme) {
    this.currentTheme = theme;
  }

  /**
   * Get toast className based on current theme
   * @returns {string} Theme-specific class name
   */
  getThemeClass() {
    if (!this.currentTheme) return "";
    // Extract theme number from "theme-0" -> "0"
    const themeNumber = this.currentTheme.replace("theme-", "");
    return `Toastify__toast--theme-${themeNumber}`;
  }

  /**
   * Create custom toast content with message (no icons)
   * @param {string} title - Toast title
   * @param {string} description - Toast description (optional)
   * @returns {JSX.Element}
   */
  createToastContent(title, description = null) {
    return (
      <div className="toast-content">
        <div className="toast-message">
          <div className="toast-title">{title}</div>
          {description && (
            <div className="toast-description">{description}</div>
          )}
        </div>
      </div>
    );
  }

  /**
   * Universal notification method - shows any toast message
   * @param {string} title - Toast title
   * @param {string} description - Toast description (optional)
   * @param {string} type - Toast type: 'success', 'error', 'warning', 'info' (default: 'info')
   * @param {object} options - Additional toast options
   */
  show(title, description = null, type = "info", options = {}) {
    const toastMethod = toast[type] || toast.info;

    toastMethod(this.createToastContent(title, description), {
      className: this.getThemeClass(),
      autoClose: 4000,
      closeOnClick: true,
      ...options,
    });
  }

  /**
   * Dismiss all toasts
   */
  dismissAll() {
    toast.dismiss();
  }
}

// Create singleton instance
const toastService = new ToastService();

export default toastService;
